import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import { apiHost } from '../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = (apiHost() || "").replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const formatRupiah = (n) => {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(n) || 0);
};

const DataGajiKaryawan = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const fetchMyGaji = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/gaji/my`, { withCredentials: true });
      setList(res.data?.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: "Gagal memuat data gaji", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyGaji();
  }, []);

  const handleViewSlip = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };

  const handlePrintSlip = (row) => {
    const data = row || selected;
    if (!data) return;
    const win = window.open("", "_blank");
    const doc = `
      <!DOCTYPE html>
      <html>
      <head><title>Slip Gaji - ${data.periode}</title></head>
      <body style="font-family: Arial; padding: 24px;">
        <h2>Slip Gaji</h2>
        <p><strong>Periode:</strong> ${data.periode}</p>
        <p><strong>Tanggal:</strong> ${data.tanggal_periode ? new Date(data.tanggal_periode).toLocaleDateString("id-ID") : "-"}</p>
        <p><strong>Karyawan:</strong> ${data.User?.username || "-"}</p>
        <p><strong>Cabang:</strong> ${data.User?.Cabang?.namacabang || "-"}</p>
        <hr/>
        <p>Gaji Pokok: ${formatRupiah(data.nominal)}</p>
        <p>Tambahan: ${formatRupiah(data.tambahan)}</p>
        <p>Potongan: ${formatRupiah(data.potongan)}</p>
        <p><strong>Jumlah Diterima: ${formatRupiah(data.jumlah)}</strong></p>
        <p>Status: ${data.status}</p>
        ${data.keterangan ? `<p>Keterangan: ${data.keterangan}</p>` : ""}
      </body>
      </html>
    `;
    win.document.write(doc);
    win.document.close();
    win.print();
    win.close();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Data Gaji Saya
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Daftar slip gaji Anda. Klik "Lihat Slip" untuk detail dan cetak.
      </Typography>

      <Card>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
        ) : list.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">Belum ada data gaji.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Periode</TableCell>
                  <TableCell>Tanggal</TableCell>
                  <TableCell align="right">Nominal</TableCell>
                  <TableCell align="right">Jumlah</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((row) => (
                  <TableRow key={row.uuid}>
                    <TableCell>{row.periode}</TableCell>
                    <TableCell>{row.tanggal_periode ? new Date(row.tanggal_periode).toLocaleDateString("id-ID") : "-"}</TableCell>
                    <TableCell align="right">{formatRupiah(row.nominal)}</TableCell>
                    <TableCell align="right">{formatRupiah(row.jumlah)}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        color={row.status === "diterima" ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewSlip(row)}>
                        Lihat Slip
                      </Button>
                      <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => handlePrintSlip(row)}>
                        Cetak
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Dialog detail slip */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detail Slip Gaji</DialogTitle>
        <DialogContent>
          {selected && (
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Periode</Typography>
              <Typography variant="body1" gutterBottom>{selected.periode}</Typography>
              <Typography variant="subtitle2" color="text.secondary">Tanggal</Typography>
              <Typography variant="body1" gutterBottom>
                {selected.tanggal_periode ? new Date(selected.tanggal_periode).toLocaleDateString("id-ID") : "-"}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">Karyawan / Cabang</Typography>
              <Typography variant="body1" gutterBottom>
                {selected.User?.username} / {selected.User?.Cabang?.namacabang || "-"}
              </Typography>
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Gaji Pokok</Typography>
                <Typography variant="body1">{formatRupiah(selected.nominal)}</Typography>
                <Typography variant="subtitle2" color="text.secondary">Tambahan</Typography>
                <Typography variant="body1">{formatRupiah(selected.tambahan)}</Typography>
                <Typography variant="subtitle2" color="text.secondary">Potongan</Typography>
                <Typography variant="body1">{formatRupiah(selected.potongan)}</Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Jumlah Diterima</Typography>
                <Typography variant="h6">{formatRupiah(selected.jumlah)}</Typography>
                <Chip label={selected.status} color={selected.status === "diterima" ? "success" : "default"} size="small" sx={{ mt: 1 }} />
              </Box>
              {selected.keterangan && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">Keterangan</Typography>
                  <Typography variant="body2">{selected.keterangan}</Typography>
                </>
              )}
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Tutup</Button>
          {selected && (
            <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handlePrintSlip}>
              Cetak Slip
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DataGajiKaryawan;
